import {
  Box,
  Switch,
  Divider,
  TextField,
  FormGroup,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { t } from 'i18next';
import { Controller, Form, FormProvider } from 'react-hook-form';

import { paths } from '@common/types';
import { NumberStepper, StatusBanner } from '@components';
import {
  NumberInputFormField,
  RequirementItem,
} from 'components/DynamicFormFields';

import { isTaskRequirement } from './common';
import { useRequirements } from './hooks';
import { ACTIVITY_MINUTES_KEYS, MAX_ACTIVITY_MINUTES } from './schema';
import { RequirementsModal } from './RequirementsModal';
import { AddOutreachAttemptModal } from './AddOutreachAttemptModal';
import { Select } from 'ui';
import {
  FAILED_OUTREACH_ATTEMPTS_UNTIL_COMPLETION,
  MAXIMUM_TEXT_AREA_LENGTH,
} from '@common/types/constants.ts';
import { isNil } from 'lodash';

interface IRequirementsProps {
  task: paths[`/task/{task_id}`]['get']['responses']['200']['content']['application/json'];
  isFormDisabled: boolean;
  setIsSavingTask: (isSaving: boolean) => void;
  isSavingTask: boolean;
  lockedBy: {
    role: string;
    name: string;
  };
  setFormValidity: (isValid: boolean) => void;
  handleUnlockLockedTask: () => void;
  isUnlockInProgress: boolean;
  isUnlockDisabled: boolean;
}

export const Requirements = ({
  task,
  isFormDisabled,
  lockedBy,
  setIsSavingTask,
  isSavingTask,
  setFormValidity,
  handleUnlockLockedTask,
  isUnlockInProgress,
  isUnlockDisabled,
}: IRequirementsProps) => {
  const {
    formMethods,
    handleChangeField,
    missing_fields,
    noteTypeOptions,
    isFormComplete,
    isCCMLimitWarningDisplayed,
    programOptions,
    handleActivityChange,
    activityLabels,
  } = useRequirements(task, setIsSavingTask, setFormValidity);

  const { control } = formMethods;
  const activityMinutes = formMethods.watch('static_activity_minutes');
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        pt: 0.8,
      }}
    >
      {isFormDisabled && lockedBy.name && (
        <StatusBanner
          text={t('common.lockedByBanner.message', {
            name: lockedBy.name,
            role: t(`roles.${lockedBy.role}`),
          })}
          variant={'warning'}
          action={handleUnlockLockedTask}
          actionText={t('common.lockedByBanner.unlock')}
          actionLoading={isUnlockInProgress}
          actionDisabled={isUnlockDisabled}
        />
      )}
      <FormProvider {...formMethods}>
        <Form
          control={formMethods.control}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            height: 'fit-content',
            borderRadius: 0,
            backgroundColor: 'white',
            position: 'relative',
            gap: '1.5rem',
          }}
        >
          {task.requirements.map((req, index) =>
            isTaskRequirement(req) ? (
              <RequirementItem
                editable={!isFormDisabled}
                key={`${req.title} ${index}`}
                {...req}
                patient_id={task.patient.id}
                handleChangeField={handleChangeField}
              />
            ) : (
              <RequirementsModal
                disabled={isFormDisabled}
                key={`${req.title} ${index}`}
                {...req}
                patient_id={task.patient.id}
                handleChangeField={handleChangeField}
                missing_fields={missing_fields}
                isSavingTask={isSavingTask}
              />
            ),
          )}
          {!task.hide_default_controls && (
            <>
              <Divider orientation='horizontal' sx={{ my: 1, width: '100%' }} />
              <Typography variant='subtitle2' sx={{ width: '100%' }}>
                Care Notes
              </Typography>

              <Box>
                {isCCMLimitWarningDisplayed && (
                  <StatusBanner
                    text={t('worklist.worklistItem.requirements.ccmWarning')}
                    variant='lightWarning'
                  />
                )}

                {!isFormComplete && (
                  <StatusBanner
                    text={t('worklist.worklistItem.requirements.careNotesInfo')}
                    variant='info'
                  />
                )}
              </Box>

              <Controller
                name='static_program_id'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    clearable
                    clearLabel='-'
                    value={field.value ?? ''}
                    type='text'
                    size='medium'
                    label='Program (optional)'
                    variant='outlined'
                    fullWidth
                    error={!isNil(error)}
                    helperText={error ? error.message : ''}
                    disabled={
                      isFormDisabled || isFormComplete || !task.program_editable
                    }
                    options={programOptions}
                    onChange={event => {
                      field.onChange(event.target.value);
                      handleChangeField(['static_program_id']);
                    }}
                  />
                )}
              />

              <Controller
                name='static_note_type'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    type='text'
                    size='medium'
                    label='Note type'
                    variant='outlined'
                    required
                    fullWidth
                    error={!isNil(error)}
                    helperText={error ? error.message : ''}
                    disabled={isFormDisabled || isFormComplete}
                    options={noteTypeOptions}
                    onChange={event => {
                      field.onChange(event.target.value);
                      handleChangeField(['static_note_type']);
                    }}
                  />
                )}
              />

              <Controller
                name='static_notes'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    sx={{ width: '100%', mb: '0.5rem' }}
                    type='text'
                    label={t('worklist.worklistItem.requirements.notes')}
                    variant='outlined'
                    fullWidth
                    required={true}
                    minRows={4}
                    multiline
                    disabled={isFormDisabled || isFormComplete}
                    onChange={event => {
                      field.onChange(event.target.value);
                      handleChangeField(['static_notes']);
                    }}
                    error={!isNil(error)}
                    helperText={
                      error
                        ? error.message
                        : `${field.value ? field.value.length + `/${MAXIMUM_TEXT_AREA_LENGTH}` : ''}`
                    }
                    slotProps={{
                      formHelperText: {
                        style: {
                          textAlign: error ? 'left' : 'right',
                          margin: 0,
                          padding: '3px 14px 0',
                        },
                      },
                    }}
                  />
                )}
              />

              {task.activity_minutes_mode && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <Typography variant='subtitle2'>
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.title',
                    )}
                  </Typography>
                  {ACTIVITY_MINUTES_KEYS.map(key => {
                    const othersSum = ACTIVITY_MINUTES_KEYS.reduce(
                      (acc, k) =>
                        k === key
                          ? acc
                          : acc + Number(activityMinutes?.[k] ?? 0),
                      0,
                    );

                    return (
                      <NumberStepper
                        key={key}
                        label={activityLabels[key]}
                        value={Number(activityMinutes?.[key] ?? 0)}
                        max={MAX_ACTIVITY_MINUTES - othersSum}
                        disabled={isFormDisabled || isFormComplete}
                        onChange={value => handleActivityChange(key, value)}
                      />
                    );
                  })}
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary' }}
                  >
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.resetNote',
                    )}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 'medium', textAlign: 'right' }}
                  >
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.remainingMinutes',
                      {
                        minutes:
                          MAX_ACTIVITY_MINUTES -
                          ACTIVITY_MINUTES_KEYS.reduce(
                            (acc, key) =>
                              acc + Number(activityMinutes?.[key] ?? 0),
                            0,
                          ),
                      },
                    )}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{ color: 'text.secondary' }}
                  >
                    {t(
                      'worklist.worklistItem.requirements.otherActivities.monthlyResetNote',
                    )}
                  </Typography>
                </Box>
              )}

              <NumberInputFormField
                name='static_recorded_minutes'
                label={t(
                  `worklist.addCustomTask.${task.activity_minutes_mode ? 'expected_minutes' : 'minutes'}`,
                )}
                onChangeCallback={() =>
                  handleChangeField(['static_recorded_minutes'])
                }
                disabled={
                  isFormDisabled || isFormComplete || task.activity_minutes_mode
                }
                optional={task.activity_minutes_mode}
              />

              <Controller
                name='static_notable_call'
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={isFormDisabled || isFormComplete}
                            onChange={e => {
                              field.onChange(e.target.checked);
                              handleChangeField(['static_notable_call']);
                            }}
                            checked={!!field.value}
                            value={field.value}
                          />
                        }
                        label='Notable'
                      />
                    </FormGroup>
                  </Box>
                )}
              />

              <Box sx={{ display: 'flex', width: '100%', mb: '2rem' }}>
                {task.outreach_task && (
                  <AddOutreachAttemptModal
                    task_id={task.id}
                    buttonText={'ADD FAILED OUTREACH ATTEMPT'}
                    disabled={
                      task.outreach_attempts.length >=
                        FAILED_OUTREACH_ATTEMPTS_UNTIL_COMPLETION ||
                      !task.has_write_permission
                    }
                  />
                )}
              </Box>
            </>
          )}
        </Form>
      </FormProvider>
    </Box>
  );
};
